import * as R from 'ramda'
import {Step2SubAction} from './Step2Actions'
import {
    CreateOrderPosition, ProductAdd, ProductAddInput, ProductAddValidation,
    Step2Data, Step2State
} from './Step2State'
import {OrderCreateFeedback} from '../OrderCreateFeedback'
// tslint:disable-next-line
import * as ProductResource from '../../../Request/ProductResource'
import {
    mandatoryNullableValidator, mandatoryStringValidator,
    nonemptyArrayValidator, stringToIntValidator, validateInputField,
    ValidationOutcome
} from '../../../Data/FormValidation'
import * as Result from '../../../Data/Result'
import {update} from '../../../Data/ObjectHelper'
import {Product, productDecoder} from '../../../Entities/Product'
import {decodeHttpResponse} from '../../../Data/Http'
import {array} from 'json-bouncer'
import {Session} from '../../../Data/Session'
import {FeedbackLoop} from '../../../Data/FeedbackLoop'

type Step2Loop = FeedbackLoop<Step2State, OrderCreateFeedback>

export function initStep2(step2: Step2Data | null): Step2Loop {
    return {
        state: {
            productAdd: initProductAdd(),
            input:
                step2 !== null ?
                    step2 :
                    {
                        positions: [],
                    },
            validation: {
                positions: null,
            },
            submissionAttempted: false,
        },
        feedback: [],
    }
}

function initProductAdd(): ProductAdd {
    return {
        productSuggestions: [],
        productSearch: '',
        input: {
            product: null,
            amount: '',
        },
        validation: {
            product: null,
            amount: null,
        },
        submissionAttempted: false,
    }
}

export function step2Reducer(
    session: Session,
    customerId: number,
    state: Step2State,
    action: Step2SubAction
): Step2Loop {
    switch (action.type) {
        case 'ProductAddProductSearchChanged':
            return {
                state: update({
                    productAdd: update({
                        productSearch: action.value,
                    }, state.productAdd),
                }, state),
                feedback: [],
            }
        case 'ProductAddFetchProductSuggestions':
            return {
                state,
                feedback: [{
                    type: 'SendRequest',
                    identifier: {
                        type: 'ProductSuggestionList',
                    },
                    httpRequest: ProductResource.listForCustomer(
                        session.manufacturer.id,
                        customerId,
                        state.productAdd.productSearch,
                    ),
                }],
            }
        case 'ProductAddProductSuggestionListResponse':
            const result = decodeHttpResponse(
                array(productDecoder()),
                action.result)
            return {
                state: update({
                    productAdd: update({
                        productSuggestions:
                            result.type === 'Ok' ? result.value : []
                    }, state.productAdd),
                }, state),
                feedback: [],
            }
        case 'ProductAddClearProductSuggestions':
            return {
                state: update({
                    productAdd: update({
                        productSuggestions: [],
                    }, state.productAdd),
                }, state),
                feedback: [],
            }
        case 'ProductAddProductChanged':
            return {
                state: update({
                    productAdd: update({
                        input: update({
                            product: action.value,
                        }, state.productAdd.input),
                        validation: update({
                            product:
                                validateInputField<Product | null, 'Mandatory', Product>(
                                    productAddValidators.product,
                                    state.productAdd.submissionAttempted,
                                    action.value),
                        }, state.productAdd.validation),
                    }, state.productAdd),
                }, state),
                feedback: [],
            }
        case 'ProductAddAmountChanged':
            return {
                state: update({
                    productAdd: update({
                        input: update({
                            amount: action.value,
                        }, state.productAdd.input),
                        validation: update({
                            amount:
                                validateInputField(
                                    productAddValidators.amount,
                                    state.productAdd.submissionAttempted,
                                    action.value),
                        }, state.productAdd.validation),
                    }, state.productAdd),
                }, state),
                feedback: [],
            }
        case 'ProductAddSubmit': {
            const validationResult = validateProductAdd(state.productAdd.input)
            return (() => {
                switch (validationResult.type) {
                    case 'Ok':
                        const newPositions =
                            R.append(
                                validationResult.value,
                                state.input.positions)
                        return {
                            state: update({
                                productAdd: initProductAdd(),
                                input: update({
                                    positions: newPositions,
                                }, state.input),
                                validation: update({
                                    positions:
                                        validateInputField(
                                            step2Validators.positions,
                                            state.submissionAttempted,
                                            newPositions),
                                }, state.validation),
                            }, state),
                            effects: [],
                            feedback: [],
                        }
                    case 'Err':
                        return {
                            state: update({
                                productAdd: update({
                                    validation: validationResult.error,
                                    submissionAttempted: true,
                                }, state.productAdd),
                            }, state),
                            effects: [],
                            feedback: [],
                        }
                }
            })()
        }
        case 'DeletePosition': {
            return {
                state: update(
                    {
                        input: update(
                            {
                                positions:
                                    R.remove(
                                        action.index,
                                        1,
                                        state.input.positions,
                                    ),
                            },
                            state.input,
                        ),
                    },
                    state,
                ),
                feedback: [],
            }
        }
    }
}

const step2Validators = {
    positions: nonemptyArrayValidator(),
}

export function validateStep2(
    state: Step2State,
): ValidationOutcome<Step2Data, Step2State> {
    const
        {input} = state,
        validation = {
            positions: step2Validators.positions(input.positions),
        }
    if (validation.positions.type === 'Ok') {
        return {
            state,
            data: {
                positions: validation.positions.value,
            },
        }
    } else {
        return {
            state: update(
                {
                    validation: {
                        positions: Result.errorToNullable(validation.positions),
                    },
                },
                state,
            ),
            data: null,
        }
    }
}

const productAddValidators = {
    product: mandatoryNullableValidator(),
    amount: (value: string) =>
        Result.andThen(
            stringToIntValidator(),
            mandatoryStringValidator()(value))
}

function validateProductAdd(input: ProductAddInput): Result.Result<ProductAddValidation, CreateOrderPosition> {
    const validation = {
        product: productAddValidators.product(input.product),
        amount: productAddValidators.amount(input.amount),
    }
    if (validation.product.type === 'Ok' &&
        validation.amount.type === 'Ok') {
        return {
            type: 'Ok',
            value: {
                product: validation.product.value,
                amount: validation.amount.value,
            },
        }
    } else {
        return {
            type: 'Err',
            error: {
                product: Result.errorToNullable(validation.product),
                amount: Result.errorToNullable(validation.amount),
            },
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/OrderCreate/Step2/Step2Reducer.ts